import React, {useEffect, useState, useCallback} from 'react';
import {observer} from 'mobx-react-lite';
import {useService} from '../../../infrastructure/service/service';
import {Download, Upload} from '@mui/icons-material';
import {useDropzone} from 'react-dropzone';
import {toast} from 'react-toastify';
import { County } from '../../../entity/model/county';
import { State } from '../../../entity/model/state';
import { BusinessRecordExcelData } from '../../../entity/dto/business-record-excel-data-dto';
import {
  TableRow,
  Table,
  TableHead,
  TableBody,
  TableCell,
  Paper,
  Typography,
  Button,
  TableContainer,
  CircularProgress
} from '@mui/material';
import Box from '@mui/material/Box';
import LoadingComponent from '../components/widget/LoadingComponent';
import TableSortLabel from '@mui/material/TableSortLabel';
import TablePagination from '../components/widget/TablePagination';

import '../../../assets/css/style.css';
import '../../../assets/css/pages/marketplace-management.css';
import 'react-loading-skeleton/dist/skeleton.css';

const BusinessRecordManagement = (): JSX.Element => {
  const {metadataService, stateService, countyService, businessRecordService} = useService();
  const [loading, setLoading] = useState<boolean>(true);
  const [counties, setCounties] = useState<County[]>([]);
  const [states, setStates] = useState<State[]>([]);
  const [uploadedData, setUploadedData] = useState<BusinessRecordExcelData[]>([]);
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);
  const [downloadLoading, setDownloadLoading] = useState<boolean>(false);
  const [isDataValid, setIsDataValid] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(0);

  const tableHeaders = ['Company Name', 'Company Type', 'Contact Person', 'Contact Email', 'Phone', 'Website', 'Is Nationwide', 'State', 'Counties (Separate by Commas)', 'Years in Business'];
  const pageSize = 100;

  useEffect(() => {
    const init = async (): Promise<void> => {
      setLoading(true);

      const allStates: State[] = await stateService.getStates();
      const allCounties: County[] = await countyService.getCounties();
            
      setStates(allStates);
      setCounties(allCounties);
    }
    init().then(() => setLoading(false));
  }, []);

  const validateUploadedData = (data: BusinessRecordExcelData[]): boolean => {
    if (!data || data.length === 0) return true;

    for (const row of data) {
      if (!row.companyName?.trim()) {
        toast.error(`Company Name is required in row ${data.indexOf(row) + 2}`);
        return false;
      }

      if (!row.contactEmail?.trim()) {
        toast.error(`Contact Email is required in row ${data.indexOf(row) + 2}`);
        return false;
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (row.contactEmail && !emailRegex.test(row.contactEmail)) {
        toast.error(`Invalid email format in row ${data.indexOf(row) + 2}: ${row.contactEmail}`);
        return false;
      }

      if (row.isNationwide && row.locations.length > 0) {
        toast.error(`Nationwide companies cannot have location data in row ${data.indexOf(row) + 2}. Please leave State and Counties columns empty for nationwide companies.`);
        return false;
      }

      if (!row.isNationwide && row.locations.length === 0) {
        toast.error(`Non-nationwide companies must have at least one location in row ${data.indexOf(row) + 2}. Please provide State and Counties data.`);
        return false;
      }

      if (row.locations && row.locations.length > 0) {
        for (const location of row.locations) {
          const validState = states.find(s => s.id === location.stateID);
          if (!validState) {
            toast.error(`Invalid state ID: '${location.stateID}' in row ${data.indexOf(row) + 2}`);
            return false;
          }

          for (const countyID of location.countyIDs) {
            const validCounty = counties.find(c => c.id === countyID && c.stateID === location.stateID);
            if (!validCounty) {
              toast.error(`Invalid county ID: '${countyID}' for state '${validState.state}' in row ${data.indexOf(row) + 2}`);
              return false;
            }
          }
        }
      }
    }

    return true;
  }

  const onDrop = useCallback(async (acceptedFiles: File[]): Promise<void> => {
    const excelFiles = acceptedFiles.filter((file: File): boolean =>
      file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
            file.type === 'application/vnd.ms-excel' ||
            file.name.endsWith('.xlsx') ||
            file.name.endsWith('.xls')
    );

    if (excelFiles.length === 0)
      return;

    if (excelFiles.length > 1)
      return;

    const file = excelFiles[0];
    const data = await metadataService.validateAndUploadBusinessRecordExcel(file, states, counties);
    setUploadedData(data);

    const totalPages = Math.ceil(data.length / pageSize);
    setTotalPages(totalPages);
    setCurrentPage(1);

    const isValid = validateUploadedData(data);
    setIsDataValid(isValid);

    if (isValid && data.length > 0)
      toast.success('Data validated successfully! You can now submit.');

  }, [metadataService, validateUploadedData]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'], 'application/vnd.ms-excel': ['.xls']},
    multiple: false}
  );

  const handleDownloadFile = async (): Promise<void> => {
    setDownloadLoading(true);
    await businessRecordService.generateTitleServiceTemplate();
    setDownloadLoading(false);
  }

  const handleUploadFile = async (): Promise<void> => {
    if (uploadedData.length === 0) return;
    if (states.length === 0) return;

    setSubmitLoading(true);
        
    const result = await businessRecordService.createBusinessRecords(uploadedData, states, counties);
    if (result) {
      setUploadedData([]);
      setCurrentPage(1);
      setTotalPages(0);
    }

    setSubmitLoading(false);
  }

  const handlePaginationChange = (event: React.ChangeEvent<unknown>, activePage: number): void => {
    setCurrentPage(activePage);
  }

  const getCurrentPageData = (): BusinessRecordExcelData[] => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return uploadedData.slice(startIndex, endIndex);
  }

  const renderTableRow = (row: BusinessRecordExcelData, index: number): JSX.Element => {
    let stateName = '-';
    let countyNames = '-';
    
    if (row.isNationwide) {
      stateName = 'Nationwide';
      countyNames = 'N/A';
    } else {
      const firstLocation = row.locations.length > 0 ? row.locations[0] : null;
      stateName = firstLocation ? states.find(s => s.id === firstLocation.stateID)?.state || firstLocation.stateID : '-';
      countyNames = firstLocation ? 
        firstLocation.countyIDs.map(countyID => counties.find(c => c.id === countyID)?.name || countyID).join(', ') : '-';
    }
    
    return (
      <TableRow key={index} className={'table-cell'}>
        <TableCell className={'no-wrap table-cell-text'} align={'left'}>{row.companyName || '-'}</TableCell>
        <TableCell className={'no-wrap table-cell-text'} align={'left'}>{row.companyType || '-'}</TableCell>
        <TableCell className={'no-wrap table-cell-text'} align={'left'}>{row.contactPerson || '-'}</TableCell>
        <TableCell className={'no-wrap table-cell-text'} align={'left'}>{row.contactEmail || '-'}</TableCell>
        <TableCell className={'no-wrap table-cell-text'} align={'left'}>{row.phone || '-'}</TableCell>
        <TableCell className={'no-wrap table-cell-text'} align={'left'}>{row.website || '-'}</TableCell>
        <TableCell className={'no-wrap table-cell-text'} align={'left'}>{row.isNationwide ? 'Yes' : 'No'}</TableCell>
        <TableCell className={'no-wrap table-cell-text'} align={'left'}>{stateName}</TableCell>
        <TableCell className={'no-wrap table-cell-text'} align={'left'}>{countyNames}</TableCell>
        <TableCell className={'no-wrap table-cell-text'} align={'center'}>{row.yearsInBusiness || '-'}</TableCell>
      </TableRow>
    );
  }

  const renderEmptyRow = (): JSX.Element => {
    return (
      <TableRow>
        <TableCell colSpan={10} align='center' className='marketplace-empty-cell'>
          <Typography id={'text-marketplace-empty'} className='empty-message'>
            No title service providers available. Upload an Excel file to see data.
          </Typography>
        </TableCell>
      </TableRow>
    );
  }

  return (
    <>
      {
        loading
          ? <LoadingComponent/>
          : <Box className={'body-box marketplace-management'}>
              <Paper className={'table-container'} elevation={0}>
                <Box className={'toolbar-container'}>
                  <Button
                    id={'btn-marketplace-download'}
                    disabled={downloadLoading}
                    className={`zitles-button ${downloadLoading ? 'disable' : ''}`}
                    variant='contained'
                    startIcon={downloadLoading ? <CircularProgress size={14} color='inherit' /> : <Download />}
                    onClick={handleDownloadFile}>
                    {
                      downloadLoading
                        ? 'Generating...'
                        : 'Download Template'
                    }
                  </Button>
                  <Box className='marketplace-toolbar-spacing' />
                  <Box {...getRootProps()}>
                    <input {...getInputProps()} />
                    <Button
                      id={'btn-marketplace-upload'}
                      className={'zitles-button'}
                      variant='contained'
                      startIcon={<Upload />}>
                      {
                        isDragActive
                          ? 'Drop Excel file here'
                          : 'Upload Template'
                      }
                    </Button>
                  </Box>
                </Box>
                <TableContainer className={'zitles-table'} component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow className={'table-header'}>
                        {
                          tableHeaders.map((header: string): JSX.Element => (
                            <TableCell key={header} className={'no-wrap'} align={header === 'Years in Business' ? 'center' : 'left'} padding='normal'>
                              <TableSortLabel
                                id={`marketplace-column-${header}`}
                                className={'table-header-text'}
                                active={true}
                                direction='desc'>
                                {header}
                              </TableSortLabel>
                            </TableCell>))
                        }
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {
                        uploadedData.length > 0
                          ? getCurrentPageData().map(renderTableRow)
                          : renderEmptyRow()
                      }
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            {
              uploadedData.length > 0 && totalPages > 1 &&
                <TablePagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onChange={handlePaginationChange}/>
            }
            {
              uploadedData.length > 0 &&
                <Box className='marketplace-submit-container'>
                  <Button
                    id={'btn-submit-metadata'}
                    disabled={submitLoading || !isDataValid}
                    className={`zitles-button submit ${submitLoading || !isDataValid ? 'disable' : ''}`}
                    variant='contained'
                    onClick={handleUploadFile}
                    startIcon={submitLoading ? <CircularProgress size={14} color='inherit' /> : null}>
                    {
                      submitLoading
                        ? 'Submitting'
                        : 'Submit'
                    }
                  </Button>
                </Box>
            }
          </Box>
      }
    </>
  );
}

export default observer(BusinessRecordManagement);
